/* Sales module (list, editor, detail). Semantic tokens only; logical properties for RTL. */

.hero {
  position: relative;
  overflow: hidden;
  margin-block-end: var(--space-6);
  padding: var(--space-6) var(--space-6) var(--space-5);
  border-radius: var(--radius-lg);
  background: var(--gradient-hero);
  color: var(--color-on-hero);
  box-shadow: var(--shadow-md);
}
.hero::after {
  content: '';
  position: absolute;
  inset-block: -40%;
  inset-inline-end: -10%;
  inline-size: 45%;
  background: radial-gradient(closest-side, rgb(255 255 255 / 0.14), transparent);
  pointer-events: none;
}
.heroRow {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
}
.heroEyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  opacity: 0.85;
}
.heroTitle {
  margin: var(--space-1) 0 0;
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  line-height: var(--leading-tight);
  color: inherit;
}
.heroSub {
  margin: var(--space-1) 0 0;
  opacity: 0.85;
  font-size: var(--text-md);
}
.heroActions {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.heroButton {
  background: var(--color-surface) !important;
  color: var(--color-primary-subtle-text) !important;
  border-color: transparent !important;
  box-shadow: var(--shadow-sm);
}

/* ------------------------------------------------------------ cards and KPIs */
.cardGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: var(--space-4);
  margin-block-end: var(--space-6);
}
.kpi {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4) var(--space-5);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  transition:
    transform var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
  overflow: hidden;
}
.kpi::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  inline-size: 4px;
  background: var(--kpi-accent, var(--color-primary));
}
.kpi:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}
.kpiLabel {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
}
.kpiValue {
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--color-heading);
  font-variant-numeric: tabular-nums;
}
.toneSuccess {
  --kpi-accent: var(--status-valid);
}
.toneWarning {
  --kpi-accent: var(--status-pending);
}
.toneDanger {
  --kpi-accent: var(--status-failed);
}
.toneInfo {
  --kpi-accent: var(--color-primary);
}

.latestTitle {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.latestSide {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
}
.sectionLabel {
  margin: 0 0 var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
}

/* ------------------------------------------------------------ register (billing list) */
.register {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-inline-size: 0;
}
/* React Aria Tabs marks its root focus-visible while any descendant has keyboard focus; the root
   wraps the whole register, so only the focused control draws a ring (RI-03). */
.register[data-focus-visible] {
  outline: none;
}
.register > .hero {
  margin-block-end: 0;
}
.heroText {
  min-inline-size: 0;
}
/* Family tabs at the inline end of the title row (React Aria Tabs). */
.familyTabs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
}
.familyTab {
  padding-block: var(--space-2);
  border-block-end: 3px solid transparent;
  color: inherit;
  opacity: 0.8;
  font-weight: var(--weight-medium);
  cursor: pointer;
  outline: none;
  transition:
    opacity var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}
.familyTab[data-hovered] {
  opacity: 1;
}
.familyTab[data-selected] {
  opacity: 1;
  border-block-end-color: var(--color-topbar-indicator);
  font-weight: var(--weight-bold);
}
.familyTab[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}
.familyPanel {
  min-inline-size: 0;
  outline: none;
}
.familyPanel[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 4px;
}
/* The last-updated line under the title: one link (DV-23). */
.latestLine {
  position: relative;
  z-index: 1;
  margin: var(--space-3) 0 0;
  min-block-size: 1.5rem;
  font-size: var(--text-sm);
}
.latestLink {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-2);
  color: inherit;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, currentColor 45%, transparent);
  text-underline-offset: 0.25em;
}
.latestLink:hover {
  text-decoration-color: currentColor;
}
.registerBody {
  display: grid;
  grid-template-columns: 17rem minmax(0, 1fr);
  gap: var(--space-5);
  align-items: start;
}
.registerBodyOnly {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}
.registerMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-inline-size: 0;
}
/* Find panel: inline start, sticky under the header, scrolls on its own. */
.findPanel {
  position: sticky;
  inset-block-start: calc(var(--app-header-size) + var(--space-4));
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-block-size: calc(100dvh - var(--app-header-size) - var(--space-8));
  overflow-y: auto;
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.findHeading {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
}
.findSearch {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.findHint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.search {
  position: relative;
  display: flex;
  align-items: center;
}
.search svg {
  position: absolute;
  inset-inline-start: var(--space-3);
  color: var(--color-text-subtle);
  pointer-events: none;
}
.search input {
  inline-size: 100%;
  block-size: var(--control-height);
  padding-inline: calc(var(--space-3) + 22px) var(--space-3);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
}
.search input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
  border-color: var(--color-primary);
}
.views {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.viewList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.viewLink {
  display: block;
  padding: var(--space-2) var(--space-3);
  border-inline-start: 3px solid transparent;
  border-radius: var(--radius-sm);
  color: var(--color-text);
  text-decoration: none;
}
.viewLink:hover {
  background: var(--color-surface-hover);
}
.viewLink[aria-current='page'] {
  border-inline-start-color: var(--color-primary);
  background: var(--color-primary-subtle);
  color: var(--color-primary-subtle-text);
  font-weight: var(--weight-semibold);
}
.facetsDisclosure {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.facetsTrigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  inline-size: 100%;
  min-block-size: var(--control-height-sm);
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-text);
  font: inherit;
  font-weight: var(--weight-semibold);
  text-align: start;
  cursor: pointer;
}
.facetsChevron {
  transition: transform var(--duration-fast) var(--ease-out);
}
.facetsDisclosure[data-expanded] .facetsChevron {
  transform: rotate(180deg);
}
.facets {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.facetChecks {
  flex-direction: column;
  gap: var(--space-1);
}
.facetFields {
  display: grid;
  gap: var(--space-3);
}
.clearFacets {
  align-self: flex-start;
}
/* The batch page's entry (WP-1b.3, AC-B2): a section at the end of the find panel on desktop, an
   underlined text link inside the find bar below 64rem — no fill, no border. */
.batchEntrySection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-block-start: var(--space-3);
  border-block-start: var(--border-width) solid var(--color-rule);
}
.batchEntryLink {
  align-self: flex-start;
  color: var(--color-link);
  font-weight: var(--weight-medium);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.batchEntryLink:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}
.facetsButton {
  align-self: flex-start;
}
.sheetDone {
  inline-size: 100%;
  min-block-size: 2.75rem;
}
/* Command bar contents (selected document, prompt, tools). */
.selectedDoc {
  color: var(--color-text);
  font-weight: var(--weight-semibold);
}
.selectPrompt {
  color: var(--color-text-muted);
}
.docTools {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.textButton {
  /* A labelled text button (RD4-04): underlined text, never a pill or an "x". */
  padding-inline: var(--space-1);
  color: var(--color-link);
  text-decoration: underline;
  text-underline-offset: 0.2em;
}
.updatedCell {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  unicode-bidi: isolate;
}
.live {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

/* ------------------------------------------------------------ table cells */
.sortButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.sortButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
}
.numberLink {
  font-family: var(--font-mono);
  font-weight: var(--weight-semibold);
  color: var(--color-link);
  text-decoration: none;
  white-space: nowrap;
  direction: ltr;
  unicode-bidi: isolate;
}
.numberLink:hover {
  text-decoration: underline;
}
.void .numberLink,
.voidText {
  text-decoration: line-through;
  color: var(--color-text-subtle);
}
.voidBadge {
  text-decoration: line-through;
}
.customerCell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.subtle {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}
.amountCell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.statusCell {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: center;
}
.badgeWrap {
  display: inline-flex;
}
.mock {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-lab-bg);
  color: var(--color-lab-text);
  border: var(--border-width) dashed var(--color-lab-border);
  font-size: 0.6875rem;
  font-weight: var(--weight-bold);
  letter-spacing: 0.02em;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.undoBar {
  position: fixed;
  /* Above a docked bottom layer (one bottom layer at a time). */
  inset-block-end: calc(var(--docked-size, 0px) + var(--space-6));
  max-inline-size: calc(100vw - 2 * var(--space-4));
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--color-heading);
  color: var(--color-surface);
  box-shadow: var(--shadow-lg);
  animation: rise var(--duration-base) var(--ease-out);
}
[dir='rtl'] .undoBar {
  transform: translateX(50%);
}
.undoBar button {
  border: 0;
  background: none;
  color: var(--color-accent);
  font: inherit;
  font-weight: var(--weight-bold);
  cursor: pointer;
}

/* ------------------------------------------------------------ editor */
.editorGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 22rem;
  gap: var(--space-5);
  align-items: start;
}
.side {
  position: sticky;
  inset-block-start: calc(var(--topbar-height) + var(--space-4));
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.card {
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  animation: rise var(--duration-base) var(--ease-out);
}
.cardHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-5);
  border-block-end: var(--border-width) solid var(--color-rule);
}
.cardTitle {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}
.cardIcon {
  display: grid;
  place-items: center;
  inline-size: 1.75rem;
  block-size: 1.75rem;
  border-radius: var(--radius-md);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
}
.cardBody {
  padding: var(--space-4) var(--space-5);
}
.fieldGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: var(--space-3) var(--space-4);
}
.numberChip {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: rgb(255 255 255 / 0.16);
  border: var(--border-width) solid rgb(255 255 255 / 0.25);
}
.numberChip strong {
  font-family: var(--font-mono);
  font-size: var(--text-xl);
  direction: ltr;
}
.numberChip span {
  font-size: var(--text-xs);
  opacity: 0.85;
}

/* Guided flow (BLOCK-1-REDESIGN §A.2): step content beside the summary rail (20rem, sticky). */
.editorBody {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20rem;
  gap: var(--space-5);
  align-items: start;
}
.stepPanel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-inline-size: 0;
  padding: var(--space-5);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}
.stepTitle {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--text-xl);
  outline: none;
}
.stepTitle:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 4px;
}
.stepCounter {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}
.stepBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-inline-size: 0;
}
.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-inline-size: 0;
  margin: 0;
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-rule);
  border-radius: var(--radius-md);
}
.groupLegend {
  padding-inline: var(--space-1);
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}
.optionalTag {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  font-weight: var(--weight-regular);
}
.kindSwitch {
  align-self: flex-start;
}
.issuerRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 18rem);
  gap: var(--space-4);
  align-items: start;
}
.issuerPanel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}
.linesHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.linesHead .note {
  margin: 0;
}
.lineCards {
  display: flex;
  flex-direction: column;
}
.extraDiscount {
  max-inline-size: 22rem;
  padding-block-start: var(--space-3);
  border-block-start: var(--border-width) solid var(--color-rule);
}
.rail {
  position: sticky;
  inset-block-start: calc(var(--app-header-size) + var(--space-4));
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-block-size: calc(100dvh - var(--app-header-size) - 6rem);
  overflow-y: auto;
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}
.railTitle {
  font-size: var(--text-md);
  color: var(--color-text-muted);
}
.railSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-block-start: var(--space-3);
  border-block-start: var(--border-width) solid var(--color-rule);
  outline: none;
}
.railSection:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}
.railHeading {
  font-size: var(--text-md);
}
.rail .numberChip {
  align-items: flex-start;
  background: var(--color-primary-subtle);
  border-color: color-mix(in srgb, var(--color-primary) 25%, transparent);
  color: var(--color-primary-subtle-text);
}
.editorHero {
  margin-block-end: 0;
}
/* Below 64rem: the rail as a one-line summary that expands in place (outside the rail, AC-E1). */
.railSummary {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-inline-size: 0;
}
.summaryToggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  inline-size: 100%;
  min-block-size: 2.75rem;
  padding: var(--space-2) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-weight: var(--weight-semibold);
  text-align: start;
  cursor: pointer;
}
.toggleIssues,
.toggleClean {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-inline-start: auto;
}
.toggleIssues {
  color: var(--status-pending-text);
}
.toggleClean {
  color: var(--status-valid);
}
.toggleChevron {
  flex: none;
  transition: transform var(--duration-fast) var(--ease-out);
}
.summaryToggle[aria-expanded='true'] .toggleChevron {
  transform: rotate(180deg);
}
.stepActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.reviewLead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-muted);
}
.reviewSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-block-start: var(--space-4);
  border-block-start: var(--border-width) solid var(--color-rule);
}
.reviewHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
}
.reviewTitle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-lg);
}
.reviewCount {
  padding: 1px var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
}
.countOpen {
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
}
.countClean {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}
/* WP-1b.4: «نسخة للمشتري بالبريد» — a neutral bordered box after the checks (no count chip, no
   «عُد إلى …»), so it never reads as a fourth check (BLOCK-1B-EMAIL-DESIGN §2.1). */
.mailBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.mailLead,
.mailNote {
  margin: 0;
  color: var(--color-text-muted);
}
.mailAddress {
  font-family: var(--font-mono);
  direction: ltr;
  unicode-bidi: isolate;
  overflow-wrap: anywhere;
}
.mailInvalid {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  color: var(--color-warning-text);
}
.mailOptions [data-testid^='mail-option-'] {
  min-block-size: 2.75rem;
}
.mailTestNote {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) dashed var(--color-lab-border);
  border-radius: var(--radius-md);
  background: var(--color-lab-bg);
  color: var(--color-lab-text);
  font-size: var(--text-sm);
}
.mailTestNote > svg {
  flex: none;
  margin-block-start: 2px;
}

/* WP-1b.4: the decorative mail icon of an e-mail line in the history. */
.eventIcon {
  margin-inline-end: var(--space-1);
  vertical-align: -2px;
}

/* WP-1b.4: the issue confirmation from the list or the detail toolbar: no copy; open step 4. */
.confirmMailNote {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
}
.confirmMailNote p {
  margin: 0;
}

/* WP-1b.4: the detail section «نسخة المشتري بالبريد»: one status line and its details. */
.delivery {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.deliveryLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
}
.deliveryFacts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.deliveryReply {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
}
.deliveryReply dt {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.deliveryReply dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.deliveryChip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
}
.deliveryChip[data-tone='neutral'] {
  background: var(--color-surface-sunken, var(--color-surface));
  color: var(--color-text);
}
.deliveryChip[data-tone='info'] {
  border-color: var(--color-info-border, var(--color-border));
  background: var(--color-info-bg);
  color: var(--color-info-text);
}
.deliveryChip[data-tone='success'] {
  border-color: var(--color-success-border);
  background: var(--color-success-bg);
  color: var(--color-success-text);
}
.deliveryChip[data-tone='lab'] {
  border-style: dashed;
  border-color: var(--color-lab-border);
  background: var(--color-lab-bg);
  color: var(--color-lab-text);
}
.deliveryChip[data-tone='danger'] {
  border-color: var(--color-danger-border, var(--color-border));
  background: var(--color-danger-bg, var(--color-surface));
  color: var(--color-danger-text);
}
.deliveryChip[data-tone='warning'] {
  border-color: var(--color-warning-border, var(--color-border));
  background: var(--color-warning-bg, var(--color-surface));
  color: var(--color-warning-text);
}

.testingBox {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) dashed var(--color-lab-border);
  border-radius: var(--radius-md);
  background: var(--color-lab-bg);
}
/* The review's definition lists and read-only lines table (BR-3): a check, not a document. */
.reviewList {
  display: grid;
  grid-template-columns: minmax(9rem, max-content) minmax(0, 1fr);
  gap: var(--space-2) var(--space-5);
  margin: 0;
}
.reviewPair {
  display: contents;
}
.reviewPair dt {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.reviewPair dd {
  display: flex;
  flex-direction: column;
  /* Each value keeps its own width, so a left-to-right code still starts at the page's start. */
  align-items: flex-start;
  gap: 2px;
  margin: 0;
  min-inline-size: 0;
  overflow-wrap: anywhere;
}
.reviewStrong {
  display: block;
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}
.reviewSub {
  display: block;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.reviewTax {
  display: block;
  color: var(--color-text-muted);
}
.cellLabel {
  display: none;
}

/* Buyer step (A.3): the question, the three choice cards, the inline form. */
.buyerBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-inline-size: 0;
}
.buyerQuestion {
  font-size: var(--text-md);
}
.inlineForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-primary);
  border-inline-start-width: 4px;
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.inlineFormTitle {
  font-size: var(--text-lg);
}

/* Customer block */
.customerSelected {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: var(--space-3);
  align-items: start;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  border: var(--border-width) solid var(--color-border);
}
.avatar {
  display: grid;
  place-items: center;
  inline-size: 2.5rem;
  block-size: 2.5rem;
  border-radius: 50%;
  background: var(--gradient-avatar);
  color: #fff;
  font-weight: var(--weight-bold);
}
.customerActions {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
/* RI-11: below 40rem the buyer card stacks, so its actions sit under the buyer's details. */
@media (max-width: 40rem) {
  .customerSelected {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .customerActions {
    grid-column: 1 / -1;
    flex-direction: row;
    flex-wrap: wrap;
  }
}
/* RI-10: controls that are fixed when the item is created read as read-only in the edit sheet. */
.readOnlyBlock {
  opacity: 0.72;
}
.readOnlyNote {
  margin: 0 0 var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.customerName {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}
.customerMeta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
.inlineActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-block-start: var(--space-3);
}

/* Lines */
.line {
  position: relative;
  display: grid;
  grid-template-columns: minmax(12rem, 2.4fr) repeat(3, minmax(6.5rem, 1fr)) minmax(7rem, 1fr) auto;
  gap: var(--space-3);
  align-items: end;
  padding: var(--space-4) 0;
  border-block-end: var(--border-width) dashed var(--color-rule);
}
.line:last-of-type {
  border-block-end: 0;
}
.lineIndex {
  grid-column: 1 / -1;
  margin-block-end: calc(-1 * var(--space-2));
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--color-primary);
}
/* Block 11 (notes-editor.md §3): a note line card's origin tag «من INV-… · البند n». */
.originTag {
  grid-column: 1 / -1;
  margin-block-end: calc(-1 * var(--space-2));
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.lineTotal {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-block-end: var(--space-2);
  font-weight: var(--weight-semibold);
}
.lineMore {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}
.lineTools {
  display: flex;
  gap: var(--space-1);
  padding-block-end: var(--space-1);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: center;
  grid-column: 1 / -1;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-primary-subtle);
  color: var(--color-primary-subtle-text);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
}
.chip button {
  display: inline-grid;
  place-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}
.chipAmount {
  font-family: var(--font-mono);
  direction: ltr;
}
.chipSelect {
  block-size: 1.75rem;
  border-radius: var(--radius-pill);
  border: var(--border-width) dashed var(--color-border-strong);
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-xs);
  padding-inline: var(--space-2);
}
.addLine {
  margin-block-start: var(--space-3);
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}
.emptyLines {
  display: grid;
  place-items: center;
  gap: var(--space-3);
  padding: var(--space-8) var(--space-4);
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-lg);
  color: var(--color-text-muted);
  text-align: center;
}

/* Totals */
.totals {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.totalRow {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  font-size: var(--text-md);
}
.totalRow dt {
  color: var(--color-text-muted);
}
.totalRow dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.deduct {
  color: var(--color-accent-text);
  font-size: var(--text-xs);
  margin-inline-start: var(--space-1);
}
.grand {
  margin-block-start: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--gradient-total);
  color: var(--color-on-hero);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.grand span {
  font-size: var(--text-sm);
  opacity: 0.9;
}
.grand strong {
  font-size: 1.6rem;
  font-variant-numeric: tabular-nums;
}
.note {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
  margin: var(--space-2) 0 0;
}

/* Readiness */
.readiness {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.readinessHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: var(--weight-semibold);
}
.readinessOk {
  color: var(--status-valid);
}
.readinessBad {
  color: var(--status-pending-text);
}
.issueList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.issueList button {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  inline-size: 100%;
  padding: var(--space-2);
  border: 0;
  border-radius: var(--radius-md);
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
  font: inherit;
  font-size: var(--text-sm);
  text-align: start;
  cursor: pointer;
}
.issueList button:hover {
  filter: brightness(0.97);
}
.warnItem {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--color-info-bg);
  color: var(--color-info-text);
  font-size: var(--text-sm);
}

/* Sticky action bar */
.actionBar {
  position: sticky;
  inset-block-end: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-block-start: var(--space-6);
  padding: var(--space-3) var(--space-5);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: color-mix(in srgb, var(--color-surface) 88%, transparent);
  backdrop-filter: blur(10px);
  box-shadow: 0 -6px 20px rgb(7 46 42 / 0.08);
}
.actionStatus {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.dot {
  inline-size: 0.5rem;
  block-size: 0.5rem;
  border-radius: 50%;
  background: var(--status-pending);
}
.dotSaved {
  background: var(--status-valid);
}

/* Pickers */
.pickList {
  list-style: none;
  margin: 0;
  padding: 0;
  max-block-size: 50vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.pickRow {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: var(--border-width) solid transparent;
  cursor: pointer;
}
.pickRow:hover {
  background: var(--color-surface-hover);
}
.pickRow[data-selected='true'] {
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
}
.pickRow input {
  inline-size: 1.1rem;
  block-size: 1.1rem;
  accent-color: var(--color-primary);
}
.sheetFooter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  inline-size: 100%;
}
.pickMeta {
  display: block;
}
.pickTitle {
  font-weight: var(--weight-medium);
}
.dialogFooter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  margin-block-start: var(--space-4);
  flex-wrap: wrap;
}
/* Item form: default taxes as a checkbox list (DV-24). */
.taxChecks {
  flex-direction: column;
  gap: var(--space-2);
}
.taxOption {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  align-items: baseline;
}
.inlineStart {
  align-self: flex-start;
}
.formStack {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.subsection {
  margin: 0;
  padding-block-start: var(--space-3);
  border-block-start: var(--border-width) solid var(--color-rule);
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

/* ------------------------------------------------------------ detail */
.detailGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 21rem;
  gap: var(--space-5);
  align-items: start;
}
.parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}
.party {
  padding: var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  border: var(--border-width) solid var(--color-border);
}
.party h3 {
  margin: 0 0 var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-primary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.party p {
  margin: 0;
  font-size: var(--text-sm);
}
.partyName {
  font-weight: var(--weight-semibold);
  font-size: var(--text-md) !important;
  color: var(--color-heading);
}
.metaGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: var(--space-3);
  margin-block: var(--space-4);
}
.meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.meta dt {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
.meta dd {
  margin: 0;
  font-weight: var(--weight-medium);
}
.lineTable {
  inline-size: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}
.lineTable th {
  text-align: start;
  padding: var(--space-2);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  font-weight: var(--weight-semibold);
  border-block-end: var(--border-width) solid var(--color-border);
}
.lineTable td {
  padding: var(--space-2);
  border-block-end: var(--border-width) solid var(--color-rule);
  vertical-align: top;
}
.lineTable .end {
  text-align: end;
}
.scrollX {
  overflow-x: auto;
}
.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
}
.timeline::before {
  content: '';
  position: absolute;
  inset-block: var(--space-2);
  inset-inline-start: 0.4rem;
  inline-size: 2px;
  background: var(--color-rule);
}
.event {
  position: relative;
  padding-inline-start: var(--space-6);
  padding-block-end: var(--space-4);
}
.event::before {
  content: '';
  position: absolute;
  inset-block-start: 0.35rem;
  inset-inline-start: 0.1rem;
  inline-size: 0.75rem;
  block-size: 0.75rem;
  border-radius: 50%;
  background: var(--event-color, var(--color-primary));
  box-shadow: 0 0 0 3px var(--color-surface);
}
.eventTitle {
  font-weight: var(--weight-semibold);
  font-size: var(--text-sm);
}
.eventMeta {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.evSuccess {
  --event-color: var(--status-valid);
}
.evWarning {
  --event-color: var(--status-pending);
}
.evDanger {
  --event-color: var(--status-failed);
}
.evNeutral {
  --event-color: var(--color-border-strong);
}
.kv {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-2) var(--space-4);
  margin: 0;
  font-size: var(--text-sm);
}
.kv dt {
  color: var(--color-text-muted);
}
.kv dd {
  margin: 0;
  text-align: end;
  overflow-wrap: anywhere;
}
.mono {
  font-family: var(--font-mono);
  direction: ltr;
  unicode-bidi: isolate;
}
.headerBadges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-block-start: var(--space-2);
}
.sectionNav {
  display: flex;
  gap: var(--space-1);
  margin-block-end: var(--space-4);
}

/* Detail toolbar under the header: every tool visible, in the fixed order (DocumentTools). */
.detailToolbar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

/* Document journey: four stages, the current one marked; horizontal from 40rem. */
.journey {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}
.journeyTitle {
  font-size: var(--text-md);
  color: var(--color-text-muted);
}
.journeyList {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.journeyStage {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  min-inline-size: 0;
  padding-block-start: var(--space-3);
  border-block-start: 4px solid var(--color-rule);
}
.stage_done {
  border-block-start-color: var(--status-valid);
}
.stage_current {
  border-block-start-color: var(--color-primary);
}
.stage_ended {
  border-block-start-color: var(--color-border-strong);
}
.journeyMarker {
  flex: none;
  display: inline-grid;
  place-items: center;
  inline-size: 2rem;
  block-size: 2rem;
  border-radius: 50%;
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
}
.stage_done .journeyMarker {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}
.stage_current .journeyMarker {
  background: var(--color-primary);
  color: var(--color-on-primary);
}
.stage_ended .journeyMarker {
  background: var(--color-neutral-bg);
  color: var(--color-neutral-text);
}
.journeyText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-inline-size: 0;
}
.journeyLabel {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}
.stage_todo .journeyLabel {
  color: var(--color-text-muted);
  font-weight: var(--weight-medium);
}
.journeyState {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  unicode-bidi: isolate;
}

/* At-a-glance tiles: buyer, document date, due from buyer. */
.glance {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
}
.summaryBox {
  max-inline-size: 26rem;
  margin-inline-start: auto;
  margin-block-start: var(--space-4);
}

/* Properties beside the Tax Authority (Properties alone, full width, on drafts). */
.pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
  align-items: start;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .undoBar,
  .kpi,
  .spin {
    animation: none;
    transition: none;
  }
}

@media (max-width: 64rem) {
  .editorGrid,
  .detailGrid {
    grid-template-columns: minmax(0, 1fr);
  }
  .side {
    position: static;
  }
  .pair,
  .editorBody,
  .issuerRow {
    grid-template-columns: minmax(0, 1fr);
  }
  .rail {
    position: static;
    max-block-size: none;
  }
  /* Register: the find panel becomes a full-width find bar; the views scroll inside a link bar. */
  .registerBody {
    grid-template-columns: minmax(0, 1fr);
  }
  .findBar {
    position: static;
    max-block-size: none;
    overflow: visible;
    gap: var(--space-3);
  }
  .findBar .findHeading:not(label) {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .findBar .viewList {
    flex-direction: row;
    gap: var(--space-1);
    overflow-x: auto;
    border-block-end: var(--border-width) solid var(--color-rule);
    scrollbar-width: thin;
  }
  .findBar .viewLink {
    flex: none;
    white-space: nowrap;
    border-inline-start: 0;
    border-block-end: 3px solid transparent;
    border-radius: 0;
  }
  .findBar .viewLink[aria-current='page'] {
    border-block-end-color: var(--color-primary);
    background: transparent;
  }
}
@media (max-width: 40rem) {
  .hero {
    padding: var(--space-5) var(--space-4);
  }
  .batchEntryLink {
    display: inline-flex;
    align-items: center;
    min-block-size: 2.75rem;
  }
  /* Detail on phones: tools two to a row (44 px), a vertical journey, stacked tiles and sections. */
  .detailToolbar {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .detailToolbar > * {
    min-block-size: 2.75rem;
    white-space: normal;
  }
  /* RIM-04: the buyer-copy controls are 44 px targets on phones, like every other tool. */
  .mailBlock button,
  .delivery button {
    min-block-size: 2.75rem;
  }
  .journeyList {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
  .journeyStage {
    padding-block: var(--space-2);
    padding-inline-start: var(--space-3);
    border-block-start: 0;
    border-inline-start: 4px solid var(--color-rule);
  }
  .stage_done {
    border-inline-start-color: var(--status-valid);
  }
  .stage_current {
    border-inline-start-color: var(--color-primary);
  }
  .stage_ended {
    border-inline-start-color: var(--color-border-strong);
  }
  .glance,
  .pair {
    grid-template-columns: minmax(0, 1fr);
  }
  .summaryBox {
    max-inline-size: none;
  }
  /* Editor on phones (390×844): one column; line cards in two columns with the item at full width. */
  .stepPanel {
    padding: var(--space-4);
  }
  .stepTitle {
    font-size: var(--text-lg);
  }
  .group {
    padding: var(--space-3);
  }
  .issuerPanel,
  .inlineForm {
    padding: var(--space-3);
  }
  .line {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  .line > .lineIndex,
  .line > .lineItem,
  .lineTools {
    grid-column: 1 / -1;
  }
  .lineTools {
    justify-content: flex-end;
  }
  .lineTools > * {
    min-inline-size: 2.75rem;
    min-block-size: 2.75rem;
  }
  /* The step bar docks at the bottom: named icons for back and save, then continue or issue. */
  .actionBar {
    position: fixed;
    inset-inline: 0;
    inset-block-end: 0;
    gap: var(--space-2);
    margin: 0;
    padding: var(--space-2) var(--space-3) calc(var(--space-2) + env(safe-area-inset-bottom, 0px));
    border-inline: 0;
    border-block-end: 0;
    border-radius: 0;
  }
  .actionStatus {
    flex: 1 1 100%;
    font-size: var(--text-xs);
  }
  .stepActions {
    flex: 1 1 100%;
    flex-wrap: nowrap;
  }
  .stepActions > * {
    min-block-size: 2.75rem;
  }
  .stepBack,
  .stepSave {
    flex: none;
    min-inline-size: 2.75rem;
    padding-inline: 0;
  }
  .stepBarText {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .stepNext {
    flex: 1 1 auto;
    white-space: normal;
  }
  /* Review: the lists in one column; each line of the table becomes a labelled card. */
  .reviewList {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
  .reviewPair dd {
    margin-block-end: var(--space-2);
  }
  .reviewLines thead {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .reviewLines,
  .reviewLines tbody {
    display: block;
  }
  .reviewLines tr {
    display: grid;
    padding-block: var(--space-2);
    border-block-end: var(--border-width) solid var(--color-rule);
  }
  .lineTable.reviewLines td {
    display: grid;
    grid-template-columns: minmax(6.5rem, 38%) minmax(0, 1fr);
    gap: var(--space-3);
    padding: var(--space-1) 0;
    border: 0;
    text-align: start;
    overflow-wrap: anywhere;
  }
  .reviewLines .cellLabel {
    display: block;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }
  .parties {
    grid-template-columns: 1fr;
  }
  .latestSide {
    align-items: flex-start;
  }
}

.kpiNote {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-4) 0 var(--space-2);
  color: var(--color-text-muted);
  font-size: 13px;
}

/* ------------------------------------------------------------ Tax Authority verdict (WP-1b.2) */
/* MF-INV-009: a refused document's verdict, field by field, labelled as the Authority's. */
.verdict {
  border-color: var(--color-danger-border);
}
.verdictIcon {
  background: var(--color-danger-bg);
  color: var(--color-danger-text);
}
.verdictLead {
  margin: 0 0 var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
.verdictSteps {
  list-style: none;
  margin: 0 0 var(--space-3);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.verdictStep {
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-rule);
  border-radius: var(--radius-md);
  min-inline-size: 0;
}
.stepFailed {
  border-color: var(--color-danger-border);
  background: var(--color-danger-bg);
}
.stepPassed {
  color: var(--color-text-muted);
}
.verdictStepHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-2);
  font-size: var(--text-sm);
}
.stepFailed .verdictStepHead {
  color: var(--color-danger-text);
}
.stepPassed .verdictStepHead svg {
  color: var(--status-valid);
}
.verdictStepName {
  font-weight: var(--weight-semibold);
  overflow-wrap: anywhere;
}
.verdictStepStatus {
  font-size: var(--text-xs);
}
.verdictCode {
  font-size: var(--text-xs);
  overflow-wrap: anywhere;
}
.verdictMessage {
  margin: var(--space-1) 0 0;
  font-size: var(--text-sm);
  color: var(--color-text);
  overflow-wrap: anywhere;
}
.verdictMessageAlt {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  overflow-wrap: anywhere;
}
.verdictFields {
  list-style: none;
  margin: var(--space-2) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.verdictField {
  padding: var(--space-2) var(--space-3);
  border-inline-start: 3px solid var(--color-danger);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  min-inline-size: 0;
}
.verdictFieldMeta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  margin: 0;
  font-size: var(--text-xs);
}
.verdictFieldMeta div {
  display: flex;
  gap: var(--space-1);
  min-inline-size: 0;
}
.verdictFieldMeta dt {
  color: var(--color-text-muted);
}
.verdictFieldMeta dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.verdictActions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  margin-block-start: var(--space-3);
  padding-block-start: var(--space-3);
  border-block-start: var(--border-width) solid var(--color-rule);
}
.verdictActions:empty {
  display: none;
}
.verdictReasonsTitle {
  margin: var(--space-2) 0 0;
  font-weight: var(--weight-semibold);
}

/*
 * Block 11: the «إجراءات لدى المصلحة» band (D-5, AC-T6): a full-width card in the page flow under
 * the journey. Lines of text, ETA's own values isolated (AC-T9), labelled 44 px buttons that wrap.
 */
.etaActions {
  inline-size: 100%;
}
.etaBand {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
/* WP-11.4 Slice 9: the related-documents bands (sales-notes.md §6; AC-T8). A list of lines, each a
   row of fields separated by «·» that wraps on a phone; never a side column or tabs (AC-T6). */
.relatedList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.relatedRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  overflow-wrap: anywhere;
}
.relatedReducible {
  margin: var(--space-3) 0 0;
}
.etaLine {
  margin: 0;
  overflow-wrap: anywhere;
}
.etaLineLabel {
  font-weight: var(--weight-semibold);
}
.etaSimTag {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}
.etaCode {
  font-family: var(--font-mono);
  font-size: 0.92em;
  direction: ltr;
  unicode-bidi: isolate;
  overflow-wrap: anywhere;
}
/* W3-04 / RB3R11-13: a quoted parameter name breaks at word boundaries, not mid-word, so its
   guillemets never strand at the start of a phone line. */
.etaCodeWrap {
  overflow-wrap: break-word;
  word-break: normal;
}
.etaParams {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-size: var(--text-sm);
}
.etaDates {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.etaButtons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-block-start: var(--space-2);
}
.etaButtons > button {
  min-block-size: 44px;
}
/* The ONE label in the product that must wrap inside its own button (the release sentence, at 390 px).
   `.button` is built for a single line — `line-height: 1`, `padding-block: 0`, `white-space: nowrap`
   — so a wrapping button has to restore BOTH the leading and the block padding; restoring only the
   wrapping left the two lines touching (0 px of ink separation) and the box at exactly 44 px because
   the lines ate the padding (B-1, role-4 BLOCKER of 2026-10-07).
   `text-wrap: balance` is a separate matter, and it is the ONLY thing here that decides WHERE the
   label breaks — leading and padding do not move a break. It equalises line lengths and has no
   syntactic knowledge: the Arabic sentence happens to balance at its em dash, which leaves
   «غير قائم» whole on the last line, while the SAME declaration balances the English into three
   lines with the em dash inside line 2. So the Arabic break is a verified OUTCOME, not a rule this
   property enforces, and it was verified in Chromium only (`apps/web/playwright.config.ts` defines
   one project); where `text-wrap` is unsupported the greedy fallback reproduces the rejected render,
   «…اعتبر الطلب غير» / «قائم». Before touching this declaration, read the Phone bullet of
   `docs/parity/visual-diff/notes-list-detail.md`: `wrapFit()` in `capture-11.spec.ts` asserts that
   the last line still carries the negation whole, so a regression fails the capture run. */
.etaWrapButton {
  white-space: normal;
  text-align: start;
  max-inline-size: 100%;
  line-height: var(--leading-normal);
  padding-block: var(--space-2);
  text-wrap: balance;
  /* A leading icon on a wrapping label belongs beside the FIRST line. `.button` centres it
     (`align-items: center`), which on the taller wrapped box puts it beside line 2 in English and in
     the gap between the lines in Arabic. */
  align-items: flex-start;
}
/* The icon (16 px, set by the control) centred on the first line box, which is `--leading-normal` of
   the font size. Only this button's icon — `Button.module.css` is untouched. */
.etaWrapButton > :first-child {
  margin-block-start: calc((var(--leading-normal) * 1em - 16px) / 2);
}
.etaDialogBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  color: var(--color-text-muted);
}
.etaDialogBody p {
  margin: 0;
}
/* RB3R11-08 / DS-2: the body's muted colour must not reach a form control's value, where a
   prefilled reason then reads as a placeholder; the field keeps the full text colour. */
.etaDialogBody :where(input, textarea, select) {
  color: var(--color-text);
}
.etaDialogActions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-block-start: var(--space-4);
}
/* T11-03: the list's decline-window line, plain text under the Tax Authority status. */
.declineLine {
  display: block;
  margin-block-start: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

/* Block 11 (WP-11.4, notes-editor.md:31-55): the note editor's step 1, «المستند المرجعي».
   The add field, the chosen-reference list (each row «أخرِج»), and the read-only buyer under it. */
.referenceAddField {
  max-inline-size: 36rem;
}
.referenceList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
.referenceItem {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-rule);
  border-radius: var(--radius-md);
}
.referenceLine {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-2);
  min-inline-size: 0;
  font-size: var(--text-sm);
}
.referenceLine [aria-hidden='true'] {
  color: var(--color-text-subtle);
}

/* Block 11 (WP-11.4, D-2, notes-editor.md:84-89): step 2 note mode — the currency, the branch and
   the activity code shown as read-only text with the «مأخوذة من الفاتورة المرجعية» tag. */
.readOnlyField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}
.readOnlyLabel {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-heading);
}
.readOnlyValue {
  font-size: var(--text-md);
  color: var(--color-text);
}
